import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, fileUrl } from "@/lib/api";
import { ArrowLeft, Pencil, Trash2, Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import VerifiedBadge from "@/components/VerifiedBadge";
import DOMPurify from "dompurify";
import { stripHtml } from "@/components/RichTextEditor";

const readingMinutes = (html) => {
  const text = stripHtml(html);
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
};

export default function BlogDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [b, setB] = useState(null);

  useEffect(() => { (async () => {
    try { const { data } = await api.get(`/blogs/${id}`); setB(data); }
    catch (err) { toast.error("Gagal memuat"); }
  })(); }, [id]);

  const del = async () => {
    if (!window.confirm("Hapus artikel?")) return;
    try { await api.delete(`/blogs/${id}`); toast.success("Dihapus"); nav("/app/blog"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Gagal"); }
  };

  if (!b) return <Layout><div className="text-bizma-subtext">Memuat...</div></Layout>;
  const canEdit = user?.user_id === b.author_id || user?.role === "admin";

  return (
    <Layout>
      <Link to="/app/blog" className="inline-flex items-center gap-2 text-sm text-bizma-subtext mb-6" data-testid="back-to-blog"><ArrowLeft size={16}/> Kembali</Link>
      <article className="felt-card overflow-hidden max-w-3xl mx-auto" data-testid="blog-detail">
        {b.photo_path && <div className="aspect-video bg-bizma-muted"><img src={fileUrl(b.photo_path)} alt="" className="w-full h-full object-cover"/></div>}
        <div className="p-6 sm:p-10">
          <div className="text-xs uppercase tracking-widest text-bizma-sage font-bold">{b.category}</div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold mt-2">{b.title}</h1>
          <div className="mt-4 flex items-center gap-3 text-sm text-bizma-subtext">
            <Link to={`/app/members/${b.author?.user_id}`} className="flex items-center gap-2 hover:text-bizma-terracotta">
              {b.author?.photo_path ? <img src={fileUrl(b.author.photo_path)} alt="" className="w-8 h-8 rounded-full object-cover"/> : <div className="w-8 h-8 rounded-full bg-bizma-sage text-white grid place-items-center text-sm font-semibold">{b.author?.name?.charAt(0)}</div>}
              <span className="font-medium text-bizma-ink">{b.author?.name}</span>
              {b.author?.is_verified && <VerifiedBadge size={12}/>}
            </Link>
            <span>· {new Date(b.created_at).toLocaleDateString("id-ID", { day:"numeric", month:"long", year:"numeric" })}</span>
            <span className="inline-flex items-center gap-1" data-testid="blog-reading-time"><Clock size={12}/> {readingMinutes(b.content)} menit baca</span>
          </div>
          <div className="mt-6 prose prose-neutral max-w-none text-[#2D2A26] leading-relaxed" data-testid="blog-content" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(b.content || "") }} />
          {canEdit && (
            <div className="mt-8 flex gap-3">
              <Link to={`/app/blog/${b.blog_id}/edit`} className="btn-outline" data-testid="edit-blog-btn"><Pencil size={14}/> Edit</Link>
              <button onClick={del} className="btn-outline text-red-600 border-red-200 hover:bg-red-50" data-testid="delete-blog-btn"><Trash2 size={14}/> Hapus</button>
            </div>
          )}
        </div>
      </article>
    </Layout>
  );
}
